import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommitteeSetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [username, setUsername] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) { setErr('Invalid invitation link'); setLoading(false); return; }
    axios.get(`${API}/committee/invite/${token}`)
      .then(r => setInfo(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Invalid or expired invitation'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (username.length < 3) return toast.error('Username must be at least 3 characters');
    if (/\s/.test(username)) return toast.error('Username cannot contain spaces');
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw !== pw2) return toast.error('Passwords do not match');
    setBusy(true);
    try {
      await axios.post(`${API}/committee/invite/accept`, { token, username, password: pw });
      toast.success('Account activated! Redirecting to login…');
      setTimeout(() => navigate('/login'), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin h-8 w-8 text-brandpurple" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy to-brandpurple flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-2xl" data-testid="set-password-page">
        <div className="flex items-center gap-2 mb-4"><Shield className="w-6 h-6 text-brandpurple" /><span className="text-xs font-bold uppercase text-brandpurple">ProcureOne Ultra</span></div>
        {err ? (
          <div className="text-center py-8">
            <div className="text-red-600 font-bold text-lg">{err}</div>
            <p className="text-sm text-slate-500 mt-2">Ask your Buyer Admin to send a fresh invitation.</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-black text-navy mb-1">Welcome, {info?.name}</h1>
            <p className="text-sm text-slate-600 mb-6">You've been invited to <strong>{info?.buyer_org}</strong> as a <strong>Committee Member</strong>. Choose your own <strong>username</strong> and <strong>password</strong> to activate your account.</p>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Email (on file)</label>
                <input type="email" value={info?.email || ''} disabled className="mt-1 w-full px-3 py-2 rounded border border-slate-200 bg-slate-50 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Username <span className="text-slate-400 normal-case font-normal">(you will log in with this)</span></label>
                <input value={username} onChange={e => setUsername(e.target.value.replace(/\s/g, ''))} className="mt-1 w-full px-3 py-2 rounded border border-slate-200 text-sm font-mono" required minLength={3} maxLength={40} data-testid="username-input" placeholder="e.g. chairman_shah" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Password (min 8 characters)</label>
                <input type="password" value={pw} onChange={e => setPw(e.target.value)} className="mt-1 w-full px-3 py-2 rounded border border-slate-200 text-sm" required data-testid="pw1" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Confirm Password</label>
                <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} className="mt-1 w-full px-3 py-2 rounded border border-slate-200 text-sm" required data-testid="pw2" />
              </div>
              <button type="submit" disabled={busy} className="w-full px-4 py-3 rounded bg-navy text-white font-bold disabled:opacity-50" data-testid="submit-pw-btn">{busy ? 'Activating…' : 'Activate My Account'}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
